import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { listCustomers, deleteCustomer, ApiError } from "../services/gridApi";
import { useDemo } from "../context/DemoContext";
import type { CustomerType } from "../types";
import CreateCustomerModal from "../components/CreateCustomerModal";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function KycStatusBadge({ status }: { status: string }) {
  const isApproved = status === "APPROVED" || status === "MANUALLY_APPROVED";
  const isPending = status === "PENDING_REVIEW";
  const isRejected = status === "REJECTED" || status === "MANUALLY_REJECTED";

  if (isApproved) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
        </svg>
        Approved
      </span>
    );
  }
  if (isPending) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-yellow-500/10 text-yellow-400">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clipRule="evenodd" />
        </svg>
        Pending
      </span>
    );
  }
  if (isRejected) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-red-500/10 text-red-400">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
        </svg>
        Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-ls-gray-800 text-ls-gray-400">
      {status.replace(/_/g, " ")}
    </span>
  );
}

export default function Customers() {
  const navigate = useNavigate();
  const { demo, getDemoCustomers } = useDemo();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<CustomerType | "ALL">("ALL");
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [showCreate, setShowCreate] = useState(false);

  // API state
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchCustomers = useCallback(async () => {
    if (demo.enabled) {
      setCustomers(getDemoCustomers());
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await listCustomers({ limit: 100 });
      setCustomers(response.data ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to load customers: ${err.message} (${err.status})`);
      } else {
        setError("Failed to load customers. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [demo.enabled, getDemoCustomers]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleDelete = async (customerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this customer? This action cannot be undone.")) {
      return;
    }
    setDeleting(customerId);
    try {
      await deleteCustomer(customerId);
      await fetchCustomers();
    } catch (err) {
      if (err instanceof ApiError) {
        alert(`Failed to delete customer: ${err.message}`);
      } else {
        alert("Failed to delete customer. Please try again.");
      }
    } finally {
      setDeleting(null);
    }
  };

  const filtered = useMemo(() => {
    return customers.filter((c) => {
      const matchesTab = activeTab === "ALL" || c.customerType === activeTab;
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        (c.id && c.id.toLowerCase().includes(searchLower)) ||
        (c.platformCustomerId && c.platformCustomerId.toLowerCase().includes(searchLower)) ||
        (c.umaAddress && c.umaAddress.toLowerCase().includes(searchLower)) ||
        (c.customerType && c.customerType.toLowerCase().includes(searchLower));
      return matchesTab && matchesSearch;
    });
  }, [customers, search, activeTab]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const tabCounts = useMemo(
    () => ({
      ALL: customers.length,
      INDIVIDUAL: customers.filter((c) => c.customerType === "INDIVIDUAL").length,
      BUSINESS: customers.filter((c) => c.customerType === "BUSINESS").length,
    }),
    [customers],
  );

  // Loading state
  if (loading && customers.length === 0) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-ls-gray-50 tracking-tight">
              Customers
            </h1>
            <p className="text-sm text-ls-gray-500 mt-1">
              Manage customers onboarded through your platform
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-24">
          <svg
            className="w-8 h-8 text-ls-sky-500 animate-spin mb-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-sm text-ls-gray-500">Loading customers...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && customers.length === 0) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-ls-gray-50 tracking-tight">
              Customers
            </h1>
            <p className="text-sm text-ls-gray-500 mt-1">
              Manage customers onboarded through your platform
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <p className="text-sm text-ls-gray-300 mb-1 font-medium">
            Unable to load customers
          </p>
          <p className="text-sm text-ls-gray-500 mb-4 max-w-md text-center">
            {error}
          </p>
          <button onClick={fetchCustomers} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-ls-gray-50 tracking-tight">
            Customers
          </h1>
          <p className="text-sm text-ls-gray-500 mt-1">
            Manage customers onboarded through your platform
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          New Customer
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 bg-ls-gray-975 border border-ls-gray-900 rounded-lg p-1">
          {(
            [
              { key: "ALL", label: "All" },
              { key: "INDIVIDUAL", label: "Individuals" },
              { key: "BUSINESS", label: "Businesses" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-ls-gray-800 text-ls-gray-50"
                  : "text-ls-gray-500 hover:text-ls-gray-300"
              }`}
            >
              {tab.label}
              <span className="ml-1.5 text-xs text-ls-gray-600">
                {tabCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        <div className="relative">
          <svg
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ls-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by ID, platform ID, UMA..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10 w-72"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-ls-gray-975 border border-ls-gray-900 rounded-lg overflow-hidden">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>UMA Address</th>
              <th>KYC Status</th>
              <th>Created</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((cust) => (
              <tr
                key={cust.id}
                className="cursor-pointer"
                onClick={() => navigate(`/customers/${cust.id}`)}
              >
                <td>
                  <div className="text-sm font-medium text-ls-gray-200">
                    {cust.fullName || cust.businessInfo?.legalName || cust.platformCustomerId}
                  </div>
                  <div className="text-[11px] font-mono text-ls-gray-500 mt-0.5">
                    {cust.platformCustomerId}
                  </div>
                </td>
                <td>
                  <span
                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      cust.customerType === "BUSINESS"
                        ? "bg-indigo-500/10 text-indigo-400"
                        : "bg-ls-gray-800 text-ls-gray-400"
                    }`}
                  >
                    {cust.customerType === "BUSINESS"
                      ? "Business"
                      : "Individual"}
                  </span>
                </td>
                <td>
                  <span className="text-sm text-ls-gray-400">
                    {cust.umaAddress || "--"}
                  </span>
                </td>
                <td>
                  <KycStatusBadge status={cust.kycStatus || "PENDING_REVIEW"} />
                </td>
                <td>
                  <span className="text-sm text-ls-gray-500">
                    {cust.createdAt ? formatDate(cust.createdAt) : "--"}
                  </span>
                </td>
                <td>
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      className="text-ls-gray-500 hover:text-ls-gray-300 p-1"
                      title="View details"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customers/${cust.id}`);
                      }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                      </svg>
                    </button>
                    <button
                      className="text-ls-gray-500 hover:text-red-400 p-1 disabled:opacity-30"
                      title="Delete customer"
                      disabled={deleting === cust.id}
                      onClick={(e) => handleDelete(cust.id, e)}
                    >
                      {deleting === cust.id ? (
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center text-ls-gray-500 py-12"
                >
                  {customers.length === 0
                    ? "No customers yet. Create one to get started."
                    : "No customers match your search."}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-ls-gray-900">
            <div className="text-xs text-ls-gray-500">
              {(page - 1) * perPage + 1}-
              {Math.min(page * perPage, filtered.length)} of {filtered.length}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded text-xs text-ls-gray-400 hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded text-xs font-medium ${
                      p === page
                        ? "bg-ls-sky-500/10 text-ls-sky-500"
                        : "text-ls-gray-400 hover:bg-white/[0.04]"
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded text-xs text-ls-gray-400 hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Customer Modal */}
      <CreateCustomerModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(customer: any) => {
          if (customer?.id) {
            navigate(`/customers/${customer.id}`);
          } else {
            fetchCustomers();
            setPage(1);
          }
        }}
      />
    </div>
  );
}
